import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, Hammer } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { MilestonesDialog } from './MilestonesDialog';
import { RenovationStatsCards } from './renovations/RenovationStatsCards';
import { RenovationProjectCard } from './renovations/RenovationProjectCard';
import { RenovationProjectDialog } from './renovations/RenovationProjectDialog';
import { EMPTY_FORM } from './renovations/constants';
import { MyTasksToggle, useMyTasksFilter, isMineForTask } from '@/components/MyTasksToggle';
import { useAuth } from '@/contexts/AuthContext';

/**
 * RenovationsView — list of renovation projects with per-org stats.
 * Refactored 2026-Feb: stats, card, and create/edit form each live
 * in their own file under `components/renovations/` so this file
 * is a slim orchestrator (fetch + mutations + composition).
 */
export const RenovationsView = ({ selectedProperty, properties }) => {
  const { user } = useAuth();
  const [onlyMine, setOnlyMine] = useMyTasksFilter();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [milestonesProject, setMilestonesProject] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // ---- data ----
  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const params = selectedProperty?.id ? { property_id: selectedProperty.id } : {};
      const response = await api.get('/renovation-projects', { params });
      setProjects(response.data);
    } catch (_) {
      toast.error('Failed to load renovation projects');
    } finally {
      setLoading(false);
    }
  }, [selectedProperty]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // ---- mutations ----
  const resetForm = useCallback(() => {
    setFormData({ ...EMPTY_FORM, property_id: selectedProperty?.id || '' });
    setStartDate(null);
    setEndDate(null);
    setEditingProject(null);
  }, [selectedProperty]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.property_id || !formData.title || !formData.project_type) {
      toast.error('Please fill in required fields');
      return;
    }
    try {
      const payload = {
        ...formData,
        estimated_budget: parseFloat(formData.estimated_budget) || 0,
        actual_cost: parseFloat(formData.actual_cost) || 0,
        progress_percent: parseInt(formData.progress_percent) || 0,
      };
      if (editingProject) {
        await api.put(`/renovation-projects/${editingProject.id}`, payload);
        toast.success('Project updated successfully');
      } else {
        await api.post('/renovation-projects', payload);
        toast.success('Project created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchProjects();
    } catch (_) {
      toast.error(editingProject ? 'Failed to update project' : 'Failed to create project');
    }
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    try {
      await api.delete(`/renovation-projects/${projectId}`);
      toast.success('Project deleted');
      fetchProjects();
    } catch (_) {
      toast.error('Failed to delete project');
    }
  };

  const handleStatusChange = async (projectId, newStatus) => {
    try {
      await api.put(`/renovation-projects/${projectId}`, { status: newStatus });
      toast.success('Status updated');
      fetchProjects();
    } catch (_) {
      toast.error('Failed to update status');
    }
  };

  const handleSavePhotos = async (projectId, photos) => {
    try {
      await api.put(`/renovation-projects/${projectId}`, { photos });
      // optimistic local update — avoid a full refetch on every add
      setProjects((prev) => prev.map((p) => (p.id === projectId ? { ...p, photos } : p)));
    } catch (_) {
      toast.error('Failed to save photos');
    }
  };

  const openEditDialog = (project) => {
    setEditingProject(project);
    setFormData({
      property_id: project.property_id,
      title: project.title,
      project_type: project.project_type,
      description: project.description || '',
      contractor_name: project.contractor_name || '',
      contractor_phone: project.contractor_phone || '',
      contractor_email: project.contractor_email || '',
      estimated_budget: project.estimated_budget?.toString() || '',
      actual_cost: project.actual_cost?.toString() || '',
      start_date: project.start_date || '',
      estimated_end_date: project.estimated_end_date || '',
      actual_end_date: project.actual_end_date || '',
      status: project.status,
      progress_percent: project.progress_percent || 0,
      notes: project.notes || '',
      permits_required: project.permits_required || false,
      permit_status: project.permit_status || '',
    });
    setStartDate(project.start_date ? new Date(project.start_date) : null);
    setEndDate(project.estimated_end_date ? new Date(project.estimated_end_date) : null);
    setDialogOpen(true);
  };

  const getPropertyName = useCallback(
    (propertyId) => properties?.find((p) => p.id === propertyId)?.name || 'Unknown Property',
    [properties]
  );

  const visibleProjects = useMemo(
    () => (onlyMine ? projects.filter((p) => isMineForTask(p, user?.id)) : projects),
    [projects, onlyMine, user?.id]
  );

  const mineCount = useMemo(
    () => projects.filter((p) => isMineForTask(p, user?.id)).length,
    [projects, user?.id]
  );

  // ---- derived stats (memoized) ----
  const stats = useMemo(() => ({
    totalProjects: visibleProjects.length,
    activeProjects: visibleProjects.filter((p) => p.status === 'in_progress').length,
    totalBudget: visibleProjects.reduce((sum, p) => sum + (p.estimated_budget || 0), 0),
    totalSpent: visibleProjects.reduce((sum, p) => sum + (p.actual_cost || 0), 0),
  }), [visibleProjects]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="renovations-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-manrope font-bold text-3xl text-slate-900 tracking-tight">
            Renovations &amp; Construction
          </h1>
          <p className="text-slate-500 mt-1">
            {selectedProperty ? selectedProperty.name : 'All Properties'} - Manage large renovation projects
          </p>
        </div>
        <div className="flex items-center gap-2">
          <MyTasksToggle
            enabled={onlyMine}
            onChange={setOnlyMine}
            count={mineCount}
            testId="renovations-my-tasks-toggle"
          />
          <Button
            onClick={() => { resetForm(); setDialogOpen(true); }}
            className="bg-slate-700 hover:bg-slate-600"
            data-testid="add-renovation-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Project
          </Button>
        </div>
      </div>

      <RenovationStatsCards {...stats} />

      {/* Projects List */}
      {visibleProjects.length === 0 ? (
        <Card className="bg-white border border-slate-100">
          <CardContent className="p-12 text-center">
            <Hammer className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              {onlyMine ? 'No projects assigned to you' : 'No Renovation Projects'}
            </h3>
            <p className="text-slate-500 mb-4">
              {onlyMine
                ? 'Toggle "My tasks" off to see the full list.'
                : 'Start tracking your renovation and construction projects'}
            </p>
            {!onlyMine && (
              <Button
                onClick={() => { resetForm(); setDialogOpen(true); }}
                className="bg-slate-700 hover:bg-slate-600"
                data-testid="create-first-renovation-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create First Project
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {visibleProjects.map((project) => (
            <RenovationProjectCard
              key={project.id}
              project={project}
              propertyName={getPropertyName(project.property_id)}
              onEdit={openEditDialog}
              onDelete={handleDelete}
              onStatusChange={handleStatusChange}
              onOpenMilestones={setMilestonesProject}
              onSavePhotos={handleSavePhotos}
            />
          ))}
        </div>
      )}

      <RenovationProjectDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editingProject={editingProject}
        properties={properties}
        formData={formData}
        setFormData={setFormData}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        onSubmit={handleSubmit}
      />

      <MilestonesDialog
        open={!!milestonesProject}
        onOpenChange={(o) => {
          if (!o) setMilestonesProject(null);
        }}
        project={milestonesProject}
        onProjectChange={(updated) => {
          setProjects((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
          setMilestonesProject(updated);
        }}
      />
    </div>
  );
};

export default RenovationsView;
